import { FRASES_DOS_ERROS_DO_OPENCODE, FRASES_DOS_ERROS_DO_TOOLKIT } from '@remoto/shared';
import { ErroApi } from './api';

/**
 * Catálogo único de mensagem de erro do painel.
 *
 * A regra da casa diz que a tela decide a frase pelo **código**, nunca pelo
 * texto cru do servidor. Cada tela vinha cumprindo isso por conta própria: eram
 * quatro funções `mensagemDe` e seis dicionários `MENSAGENS`, dois deles
 * idênticos byte a byte. O resultado é o que se espera de seis cópias —
 * `sem_permissao` tinha quatro frases diferentes e `vps_offline`, três, sem que
 * nada apontasse a divergência. E duas telas ainda caíam em `erro.message`,
 * mostrando o texto do servidor que a regra proíbe.
 *
 * Aqui ficam as frases que valem em qualquer lugar. Onde a tela precisa de uma
 * frase própria para o mesmo código — "seu perfil não pode **enviar áudio**" —
 * ela passa em `extras`, que vence o catálogo.
 */
const CATALOGO: Record<string, string> = {
  // Disco e caminho
  arquivo_negado: 'Este arquivo guarda credencial e não sai da máquina.',
  fora_da_raiz: 'Esta pasta não está liberada nesta máquina.',
  cwd_fora_da_raiz: 'Esta pasta não está liberada nesta máquina.',
  somente_leitura: 'Esta raiz é somente leitura.',
  nao_encontrado: 'Não encontrei este caminho na máquina.',
  conflito_de_conteudo: 'O arquivo mudou na máquina desde que você o abriu.',
  disco_insuficiente: 'Não há espaço em disco suficiente nesta máquina.',
  // Do armazenamento, não da máquina da conversa: o anexo de 5 GiB é o primeiro
  // que pode não caber na reserva do disco da VPS, e "nesta máquina" mandaria
  // a pessoa olhar o PC dela.
  armazenamento_cheio: 'O servidor está sem espaço para este anexo agora.',
  // O daemon da máquina é anterior ao que aceita anexo grande: ele só recusaria
  // DEPOIS do upload inteiro, então a API recusa já no preparo. O conserto é
  // atualizar o daemon, não anexar de novo.
  maquina_nao_aceita_anexo_grande:
    'A máquina desta conversa ainda não aceita anexos acima de 5 MB. Atualize o Remoto nela.',

  // Máquina e sessão
  vps_offline: 'A máquina está desligada.',
  sessao_observada:
    'Esta conversa é do VS Code — o painel só a observa, e encerrá-la é de quem está no editor. ' +
    'Use Arquivar para tirá-la da lista.',
  /*
    Dizia "Encerre uma pela lista" — e este é o texto que aparece no momento
    exato em que alguém precisa de vaga. Mandava jogar fora uma conversa para
    abrir espaço, quando Adormecer devolve a mesma vaga guardando tudo. Foi
    assim que cinco conversas foram encerradas num dia de falta de vaga.
  */
  capacidade_esgotada:
    'Esta máquina já está no limite de sessões simultâneas. Use Adormecer numa conversa parada: ' +
    'devolve a vaga e guarda o histórico — quem conta a vaga é o daemon da máquina, não o painel.',
  vps_sem_memoria:
    'Esta máquina está sem memória para mais uma sessão. Adormecer uma conversa parada devolve ' +
    'a memória dela.',
  sessao_encerrada: 'Esta sessão já foi encerrada.',
  sem_capacidades: 'A sessão não está viva nesta máquina.',
  maquina_online: 'O agente desta máquina está conectado. Desligue-o antes de remover.',

  // Permissão e modo
  sem_permissao: 'Seu perfil não pode fazer isso.',
  fable_sem_confirmacao: 'Este modelo cobra crédito extra. Marque a confirmação para continuar.',
  // Troca de modelo: a regra é do motor da sessão (Claude: política da casa;
  // Codex: o catálogo que a máquina publicou).
  modelo_desconhecido: 'Este modelo não está disponível para o motor desta sessão.',
  esforco_desconhecido: 'Este nível de esforço não existe para o motor desta sessão.',
  catalogo_do_codex_ausente:
    'A máquina ainda não informou os modelos do Codex. Espere ela reconectar e tente de novo.',
  modo_perigoso_sem_confirmacao:
    'Este modo dispensa aprovações. Marque a confirmação para continuar.',
  /*
    Não é falta de permissão, e a mensagem diz isso: nem ADMIN consegue. Na VPS
    o sandbox é a única barreira do Codex — não há hook de ferramenta naquele
    motor —, e `bypassPermissions` é justamente o modo que desliga o sandbox.
  */
  anexo_na_maquina_desligada:
    'O anexo está guardado na máquina desta conversa, e ela está desligada agora. Ligue a máquina para abri-lo.',
  bypass_total_desligado:
    'A mestre só nasce em bypass onde o bypass total está ligado (BYPASS_TOTAL, na API e no daemon desta máquina).',
  modo_indisponivel_na_vps:
    'Na VPS o bypass do Codex só existe com o bypass total ligado (BYPASS_TOTAL, na API e no daemon), ' +
    'e mesmo assim dentro do sandbox. Use Automático, que decide sem sair do sandbox.',
  /*
    Dizia "Encerre e continue numa nova" — texto anterior ao botão Retomar, que
    mandava exatamente o gesto errado: encerrar joga fora a conversa que dormir
    tinha acabado de guardar. Quem lê isto está a um clique de recuperar tudo.
  */
  sessao_dormindo: 'Esta conversa dormiu. Use Retomar para trazer o histórico inteiro de volta.',
  // A recusa é deliberada e a máquina está ótima: falar em rede aqui mandaria
  // investigar o lugar errado.
  sessao_em_turno:
    'A conversa está trabalhando agora. Espere o turno terminar ou interrompa antes de adormecer.',
  sessao_nao_observada: 'Esta conversa já é do painel — não há o que continuar daqui.',
  // A máquina respondeu: o que falta é a chave do daemon. Dizer "offline"
  // mandaria a pessoa investigar rede.
  continuar_desligado: 'Esta máquina não oferece continuar do painel.',
  pedido_expirado: 'Este pedido expirou.',
  pedido_ja_decidido: 'Alguém já respondeu este pedido.',
  resposta_incompativel: 'Esta resposta não serve para este tipo de pedido.',

  // Contas
  email_em_uso: 'Já existe uma conta com esse e-mail.',
  nome_em_uso: 'Já existe uma máquina com esse nome.',
  ultimo_admin: 'Esta é a última conta de administrador ativa: promova outra antes.',
  credenciais_invalidas: 'E-mail ou senha não conferem.',
  totp_invalido: 'O código não confere. Tente o próximo.',
  /*
    O desafio é a etapa entre a senha e o código, e quando ele expira a tela
    anterior não serve mais para nada — o QR que está ali já não vale. Por isso
    a frase manda voltar, e a tela VOLTA sozinha: deixar a pessoa diante de um
    QR morto foi o que fez a ativação parecer quebrada em produção.
  */
  desafio_expirado: 'Esta etapa demorou demais e expirou. Entre de novo para continuar.',
  totp_ja_ativo: 'O segundo fator já está ativo nesta conta.',
  codigo_invalido: 'Este código de recuperação não serve.',
  familia_revogada: 'Sua sessão expirou. Entre de novo.',
  redefinicao_invalida: 'Este link de redefinição não vale mais. Peça um novo na tela de entrada.',
  sessao_substituida: 'Sua conta entrou em outro aparelho. Este perfil permite um acesso por vez.',

  // Áudio e trabalhos
  tamanho_excedido: 'O arquivo passa do tamanho máximo permitido.',
  objeto_divergente: 'O envio não chegou inteiro. Tente de novo.',
  sigilosa_sem_provedor: 'Ainda não há transcrição local para gravação sigilosa.',
  gravacao_nao_concluida: 'Espere a transcrição terminar.',
  gravacao_finalizada: 'Esta gravação já foi finalizada.',
  sem_reproducao: 'Esta gravação ainda não tem áudio para tocar.',
  trabalho_concluido: 'Este trabalho já terminou.',
  anexo_invalido: 'Este anexo não pertence a esta sessão.',
  token_expirado: 'O endereço expirou. Recarregue a página.',

  /*
    O terceiro motor (F17). As frases moram no `shared`, ao lado dos códigos,
    porque três telas as usam — criar sessão, criar mestre e trocar de motor —
    e uma cópia por tela divergiria na primeira revisão de texto. Aqui elas só
    entram no catálogo, para valer em qualquer `mensagemDoErro`.
  */
  ...FRASES_DOS_ERROS_DO_OPENCODE,

  /*
    O toolkit (F3.7). Pelo mesmo motivo do OpenCode: as frases moram no
    `shared`, ao lado dos códigos, e a tela do Toolkit e o canal das sessões
    dizem a mesma coisa para o mesmo código.
  */
  ...FRASES_DOS_ERROS_DO_TOOLKIT,
};

/**
 * A frase de um CÓDIGO, sem precisar de um `ErroApi`.
 *
 * Existe porque nem todo código chega como exceção: o fim de uma entrega em
 * segundo plano vem pelo socket (`sessao.entrega`), com o mesmo par
 * `{codigo, mensagem}` que a rota devolveria. Sem um segundo caminho, a mesma
 * falha teria uma frase no envio e outra na entrega. Código ausente ou
 * desconhecido cai no `padrao`, e o texto cru do servidor nunca aparece.
 */
export function mensagemDoCodigo(
  codigo: string | undefined,
  padrao = 'Não foi possível concluir.',
  extras?: Record<string, string>,
): string {
  if (codigo === undefined) return padrao;
  return extras?.[codigo] ?? CATALOGO[codigo] ?? padrao;
}

/** O que mostrar para um erro, sempre em pt-BR e sempre pelo código. */
export function mensagemDoErro(
  erro: unknown,
  padrao = 'Não foi possível concluir.',
  extras?: Record<string, string>,
): string {
  if (!(erro instanceof ErroApi)) return padrao;
  return mensagemDoCodigo(erro.codigo, padrao, extras);
}
